import React, { useState, useEffect, useCallback } from 'react';
import { FiSearch, FiTrash2, FiRefreshCw } from 'react-icons/fi';
import { toast } from 'react-toastify';
import api from '../../services/api';

const PER_PAGE = 15;

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/api/admin/users');
      setUsers(res.data || []);
    } catch {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchUsers(); }, [fetchUsers]);

  const filtered = users.filter(u => {
    const matchSearch = !search ||
      (u.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (u.phone || '').includes(search);
    const matchRole = roleFilter === 'ALL' || (u.role || '').toString() === roleFilter;
    return matchSearch && matchRole;
  });

  const paged = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);
  const totalPages = Math.ceil(filtered.length / PER_PAGE);

  const toggleStatus = async (id, currentStatus) => {
    const newStatus = currentStatus === 'ACTIVE' ? 'BLOCKED' : 'ACTIVE';
    try {
      await api.put(`/api/admin/users/${id}/status`, { status: newStatus });
      setUsers(prev => prev.map(u => u.id === id ? { ...u, status: newStatus } : u));
      toast.success(`User ${newStatus === 'ACTIVE' ? 'unblocked' : 'blocked'}`);
    } catch { toast.error('Failed to update user status'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await api.delete(`/api/admin/users/${id}`);
      setUsers(prev => prev.filter(u => u.id !== id));
      toast.success('User deleted');
    } catch { toast.error('Delete failed'); }
  };

  const activeCount = users.filter(u => (u.status || '').toString() === 'ACTIVE').length;
  const adminCount = users.filter(u => (u.role || '').toString() === 'ADMIN').length;

  return (
    <div>
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        {[
          { label: 'Total Users', value: users.length, emoji: '👥', color: '#EFF6FF' },
          { label: 'Active Users', value: activeCount, emoji: '✅', color: '#E8F8F5' },
          { label: 'Admins', value: adminCount, emoji: '🔑', color: '#F5F3FF' },
          { label: 'Blocked', value: users.filter(u => (u.status || '').toString() === 'BLOCKED').length, emoji: '🚫', color: '#FFF1F2' },
        ].map(c => (
          <div key={c.label} className="stat-card">
            <div className="stat-icon" style={{ background: c.color }}>{c.emoji}</div>
            <div className="stat-info"><p className="stat-label">{c.label}</p><p className="stat-value">{c.value}</p></div>
          </div>
        ))}
      </div>

      <div className="admin-card">
        <div className="admin-card-header">
          <h3>Users <span style={{ fontSize: 13, color: 'var(--text-muted)', fontWeight: 400 }}>({filtered.length})</span></h3>
          <div className="admin-card-actions">
            <div style={{ display: 'flex', gap: 6 }}>
              {['ALL', 'USER', 'ADMIN'].map(r => (
                <button key={r} onClick={() => { setRoleFilter(r); setPage(1); }} style={{
                  padding: '6px 14px', borderRadius: 20, border: '1px solid', fontSize: 12, fontWeight: 700, cursor: 'pointer',
                  background: roleFilter === r ? 'var(--huge-purple)' : 'var(--bg-card)',
                  borderColor: roleFilter === r ? 'var(--huge-purple)' : 'var(--border)',
                  color: roleFilter === r ? 'white' : 'var(--text-secondary)',
                }}>{r === 'ALL' ? 'All' : r}</button>
              ))}
            </div>
            <div className="admin-search">
              <FiSearch />
              <input placeholder="Search by name, email or phone..." value={search} onChange={e => { setSearch(e.target.value); setPage(1); }} />
            </div>
            <button onClick={fetchUsers} style={{ padding: '8px', borderRadius: 8, border: '1px solid var(--border)', background: 'var(--bg-card)', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}>
              <FiRefreshCw size={15} />
            </button>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
            <div style={{ width: 32, height: 32, border: '3px solid #E5E7EB', borderTopColor: '#7C3AED', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 12px' }} />
            Loading users...
          </div>
        ) : (
          <>
            <div className="admin-table-wrap">
              <table className="admin-table">
                <thead>
                  <tr><th>User</th><th>Phone</th><th>Role</th><th>Joined</th><th>Status</th><th>Actions</th></tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr><td colSpan={6} style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)' }}>
                      {users.length === 0 ? 'No users registered yet' : 'No users match the filter'}
                    </td></tr>
                  ) : paged.map(user => {
                    const role = (user.role || 'USER').toString();
                    const status = (user.status || 'ACTIVE').toString();
                    return (
                      <tr key={user.id}>
                        <td>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                              width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                              background: role === 'ADMIN' ? 'linear-gradient(135deg,#EC4899,#7C3AED)' : 'linear-gradient(135deg,#7C3AED,#06B6D4)',
                              color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 15,
                            }}>{(user.name || 'U').charAt(0).toUpperCase()}</div>
                            <div>
                              <p style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{user.name}</p>
                              <p style={{ fontSize: 12, color: 'var(--text-muted)' }}>{user.email}</p>
                            </div>
                          </div>
                        </td>
                        <td style={{ fontSize: 13 }}>{user.phone || '-'}</td>
                        <td><span className={`table-badge ${role === 'ADMIN' ? 'badge-pink' : 'badge-blue'}`}>{role}</span></td>
                        <td style={{ fontSize: 13 }}>{user.joinDate || '-'}</td>
                        <td>
                          <button onClick={() => role !== 'ADMIN' ? toggleStatus(user.id, status) : null} style={{
                            padding: '4px 12px', borderRadius: 20, border: 'none', fontSize: 11, fontWeight: 700,
                            cursor: role === 'ADMIN' ? 'default' : 'pointer',
                            background: status === 'ACTIVE' ? 'rgba(16,185,129,0.12)' : status === 'BLOCKED' ? 'rgba(239,68,68,0.12)' : 'var(--bg-card)',
                            color: status === 'ACTIVE' ? '#10B981' : status === 'BLOCKED' ? '#EF4444' : 'var(--text-muted)',
                          }}>{status}</button>
                        </td>
                        <td>
                          {role !== 'ADMIN' && (
                            <button className="table-action-btn delete" onClick={() => handleDelete(user.id)}><FiTrash2 /></button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {totalPages > 1 && (
              <div className="admin-pagination">
                <p>Showing {(page - 1) * PER_PAGE + 1}–{Math.min(page * PER_PAGE, filtered.length)} of {filtered.length}</p>
                <div className="admin-pagination-btns">
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                    <button key={p} className={`page-btn${p === page ? ' active' : ''}`} onClick={() => setPage(p)}>{p}</button>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
